import React, {memo, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {PlusOutlined, DeleteOutlined} from '@ant-design/icons';
import {List, Input, Card, Row, Col} from 'antd';
import styles from './index.less';
import UploadImage from './UploadImage';
import {useCrudEvidencesRunAudit} from '@/modules/auditorAudits/hooks/useAuditorAudits';
import {useParams} from 'react-router-dom';
import {showConfirm} from '@/hooks/useCrud';
import {Blob, FormData} from 'formdata-node';
import {FormDataEncoder} from 'form-data-encoder';
import {useRunAuditOnlineStatus} from '@/contexts/RunAuditOnlineStatusProvider';
const {TextArea} = Input;

const PicturesWall = ({
  translation,
  runAudit,
  question,
  sectionId,
  incidence,
  setCheckedIssue,
  disabled
}) => {
  const {id} = useParams();
  const {t} = useTranslation(translation || 'common');
  const { online } = useRunAuditOnlineStatus();
  const [fileList, setFileList] = useState(runAudit ? question?.issues?.find((el) => el?._id === incidence?._id)?.evidences : []);
  // const {update} = useCrudQuestions(question?._id, sectionId);
  const {create} = useCrudEvidencesRunAudit();

  /** On Submit form */
  const onSubmit = useCallback(async (newEvidences) => {
    const issues = await question?.issues?.map((issue) => {
      if (issue?._id === incidence?._id) {
        return {...issue, evidences: newEvidences};
      }
      return issue;
    });
    setFileList(newEvidences);
    setCheckedIssue(issues);

    if(runAudit) {
      const form = new FormData();
      form.set('evidences', JSON.stringify(newEvidences));
      form.set('issue', JSON.stringify(incidence?._id));
      const encoder = new FormDataEncoder(form);
      const options = {
        headers: encoder.headers,
      };
      await create({
        auditId: id,
        sectionId,
        questionId: question?._id,
        options,
        issue: incidence?._id,
        evidences: newEvidences,
        data: new Blob(encoder, {type: encoder.contentType})
      });
    }
  }, [create, id, incidence?._id, question?._id, question?.issues, runAudit, sectionId, setCheckedIssue]);

  /** On remove evidence */
  const onRemove = useCallback(async (idx) => {
    const newEvidences = [...fileList];
    newEvidences?.splice(idx, 1);
    await onSubmit(newEvidences);
  },[fileList, onSubmit]);

  const onRemoveImage= useCallback(async (idx) => {
    const evidences = [...fileList];
    evidences[idx] = {...evidences[idx], pdfThumb:null, url : null};
    await onSubmit(evidences);
  },[fileList, onSubmit]);

  /** Add new evidence to the array */
  const onAddEvidence = useCallback(async (e) => {
    e.stopPropagation();
    const newEvidences = fileList?.length ?
      [...fileList, { type: 'photo' }] :
      [{ type: 'photo' }];
    setFileList(newEvidences);
  }, [fileList]);

  /** On change observation name */
  const onChangeObservation = useCallback(async (note, file, idx) => {
    const newEvidences = [...fileList];
    newEvidences[idx] = {...file, observation: note};
    setFileList(newEvidences);
  }, [fileList]);

  /** On save observation name */
  const onBlurObservation = useCallback(async (note, file, idx) => {
    const newEvidences = [...fileList];
    newEvidences[idx] = {...file, observation: note};
    await onSubmit(newEvidences);
  }, [fileList, onSubmit]);

  /** On Upload image */
  const onUpload = useCallback(async (data, idx) => {
    const newFileList = fileList;
    newFileList[idx] = {...newFileList[idx], url: data?.image || data, pdfThumb: data?.pdfThumb};
    await onSubmit(newFileList);
  }, [fileList, onSubmit]);

  return (
    <div className={styles.wrapperPictures}>
      <Card
        className={'mb-2 rounded'}
        bodyStyle={{padding: '6px', background: '#FAFAFA'}}
      >
        <div className="font-bold my-2">{t('details.evidences')}:</div>
        {fileList?.length > 0 && <List itemLayout="vertical"
          size="large"
          pagination={false}
          dataSource={fileList}
          actions={[<div key="1">{'action'}</div>,]}
          renderItem={(file, idx) => (
            <Card
              className="mb-2 rounded"
              bodyStyle={ { padding: '8px 6px 0px 6px' } }
            >
              <Row key={ file?._id } gutter={ 8 } align='middle'>
                <Col xs={ 22 } md={ 23 } >
                  <Row gutter={ 8 }>
                    <Col sm={24} flex='auto' className='mb-2'>
                      <TextArea
                        rows={ 4 }
                        allowClear
                        value={ file?.observation }
                        placeholder={ `${t('templates.leaveCommentAssociatedWithTheEvidence')}...` }
                        onChange={ (e) => onChangeObservation(e?.target?.value, file, idx) }
                        onBlur={ (e) => onBlurObservation(e?.target?.value, file) }
                        disabled={ disabled }
                      />
                    </Col>
                    <Col flex="130px">
                      <UploadImage
                        value={ file }
                        onChange={ (data) => onUpload(data, idx) }
                        onRemove={() => {
                          online ? showConfirm(t, '', () => onRemoveImage(idx)) : onRemoveImage(idx);
                        }}
                        disabled={ disabled }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={ 2 } md={ 1 } className='flex justify-center'>
                  <DeleteOutlined
                    style={ { color: 'red' } }
                    onClick={() => {
                      online ? showConfirm(t, '', () => onRemove(idx)) : onRemove(idx);
                    }}
                  />
                </Col>
              </Row>
            </Card>
          )}
        />}
      </Card>
      <div
        onClick={(e) => !disabled && onAddEvidence(e)}
        className={classNames(styles.uploadPictureButton, disabled && 'opacity-50 cursor-not-allowed')}
      >
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>{t('addEvidence')}</div>
      </div>
    </div>
  );
};

PicturesWall.propTypes = {
  translation: PropTypes.string,
  runAudit: PropTypes.bool,
  sectionId: PropTypes.string,
  question: PropTypes.object,
  incidence: PropTypes.object,
  setCheckedIssue: PropTypes.func,
  disabled: PropTypes.bool
};

export default memo(PicturesWall);
